import React, { useState, useEffect, useRef } from 'react'
import { Table } from '@mantine/core'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { statusColors } from './trade-enty-page-columnDefs'
import { masterTradeStatusLabel } from './utils/master-trade-status'
import { CustomLoader } from 'app/views/components/loader/loader'

interface Props {
  data: MasterTradeParams[]
  searchValue: string
  onTradeSelect?: (trade: MasterTradeParams) => void
  onClose?: () => void
  /** The trades are still being fetched (a page that loads them on the first keystroke). */
  loading?: boolean
  /** Told, after render, whenever there is a search value — never during render, since it may dispatch. */
  onSearchStart?: () => void
}

export function TradeSearchTable({
  data,
  searchValue,
  onTradeSelect,
  onClose,
  loading = false,
  onSearchStart,
}: Props) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const selectedRowRef = useRef<HTMLTableRowElement>(null)

  // On every change of the value, not once: the search box outlives a tab
  // switch on the Trade detail page, and a failed load must get another go.
  useEffect(() => {
    if (searchValue.trim()) onSearchStart?.()
  }, [searchValue, onSearchStart])

  useEffect(() => {
    // Reset to first item when data changes
    setSelectedIndex(0)
  }, [data])

  useEffect(() => {
    // Scroll selected row into view
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (data.length === 0) return

      switch (e.key) {
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < data.length) {
            onTradeSelect?.(data[selectedIndex])
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => Math.min(prev + 1, data.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => Math.max(prev - 1, 0))
          break
        case 'Escape':
          e.preventDefault()
          onClose?.()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [data, selectedIndex, onTradeSelect, onClose])

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  const highlightText = (text: string) => {
    if (!searchValue || !text) return text

    const escapedSearch = escapeRegExp(searchValue)
    const parts = text.split(new RegExp(`(${escapedSearch})`, 'gi'))
    return parts.map((part, index) =>
      part.toLowerCase() === searchValue.toLowerCase() ? (
        <span key={index} className="tradeSearchResult">
          {part}
        </span>
      ) : (
        part
      ),
    )
  }

  if (data.length === 0 && loading) {
    // the app's spinner, at a third of its size, in place of the message
    return (
      <div
        data-testid="trade-search-loading"
        className="searchMessageText"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60 }}
      >
        <div style={{ transform: 'scale(0.33)', transformOrigin: 'center' }}>
          <CustomLoader />
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="searchMessageText">
        {searchValue.trim().length < 2
          ? 'Type at least 2 characters to search...'
          : 'No matching trades found...'}
      </div>
    )
  }

  return (
    <div className="tradeTableContainer">
      <div className="tradeTableScroll">
        <Table className="tradeTable" highlightOnHover>
          <thead>
            <tr>
              <th>Trade ID</th>
              <th>Internal Trade ID</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {data.map((trade, index) => (
              <tr
                key={trade.id}
                ref={selectedIndex === index ? selectedRowRef : null}
                onClick={() => {
                  onTradeSelect?.(trade)
                }}
                style={{
                  cursor: 'pointer',
                  backgroundColor:
                    selectedIndex === index ? '#e3e7ff' : undefined,
                }}
              >
                <td style={{ whiteSpace: 'nowrap' }}>
                  {highlightText(trade.systemId || 'N/A')}
                </td>
                <td>{highlightText(trade.clientId || 'N/A')}</td>
                <td
                  style={{
                    color:
                      statusColors[
                        masterTradeStatusLabel(trade.status).toLowerCase() || 'open'
                      ],
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '19px',
                    textAlign: 'center',
                  }}
                >
                  {masterTradeStatusLabel(trade.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
